import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';

import { isOpenAiAdsSuppressed, OPENAI_ADS_PIXEL_ID } from '@/constants/openai-ads';
import { captureOpprefFromUrl } from '@/utils/openai-ads-attribution';
import { runAfterLoadWhenIdle } from '@/utils/run-after-load-when-idle';

/**
 * OpenAI Ads measurement pixel (`oaiq`).
 *
 * No consent gate (SCRUM-1954, decision of 20/10): the cookie notice is informational, so the pixel
 * loads on arrival and the snippet keeps the explicit `oaiq("consent", true)`. Two gates remain:
 *
 * 1. **Staging.** `isOpenAiAdsSuppressed` keeps non-production hosts out of the ad account.
 *
 * 2. **`load` + idle.** Same treatment the GTM container gets in `_app.tsx`, for the same measured
 *    reason: a third-party script that finishes downloading before the paint lands inside the window
 *    Lighthouse charges against LCP. The pixel is small, but it is also never urgent — nothing on the
 *    first screen depends on it, and `oaiq` queues calls made before the SDK arrives.
 *
 * The `oppref` capture is NOT behind the idle gate. It only reads our own URL into sessionStorage and
 * it must happen on the landing page (the param is gone after the first client-side navigation).
 */

/** The installation snippet from the OpenAI docs, verbatim except for the interpolated pixel id. */
const pixelSnippet = (id: string) => `
  (function (w, d, s, u) {
    if (w.oaiq) return;
    var q = function () { q.q.push(arguments); };
    q.q = [];
    w.oaiq = q;
    var js = d.createElement(s);
    js.async = true;
    js.src = u;
    var f = d.getElementsByTagName(s)[0];
    f.parentNode.insertBefore(js, f);
  })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");

  oaiq("consent", true);
  oaiq("init", { pixelId: ${JSON.stringify(id)} });
`;

export function OpenAiAdsPixel() {
  const router = useRouter();
  const [isPixelMounted, setIsPixelMounted] = useState(false);

  // The click parameter lives on the landing URL only. Capturing it on every route change (not just on
  // mount) covers the visitor who lands on `/` and converts two navigations later on `/trial`.
  useEffect(() => {
    captureOpprefFromUrl();

    router.events.on('routeChangeComplete', captureOpprefFromUrl);
    return () => router.events.off('routeChangeComplete', captureOpprefFromUrl);
  }, [router.events]);

  useEffect(() => {
    // Not rendering the snippet at all is what keeps staging out of the ad account: no `oaiq`, so the
    // pixel half cannot fire even if something calls `measure` directly.
    if (!OPENAI_ADS_PIXEL_ID || isOpenAiAdsSuppressed()) return undefined;

    return runAfterLoadWhenIdle(() => setIsPixelMounted(true));
  }, []);

  if (!OPENAI_ADS_PIXEL_ID || isOpenAiAdsSuppressed() || !isPixelMounted) return null;

  return <Script id='openai-ads-pixel' strategy='afterInteractive' dangerouslySetInnerHTML={{ __html: pixelSnippet(OPENAI_ADS_PIXEL_ID) }} />;
}
